/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState, useEffect } from 'react';

import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Heading from '@zenbusiness/zui/Heading';
import LinearProgress from '@zenbusiness/zui/LinearProgress';
import Link from '@zenbusiness/zui/Link';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { ContainerGrid, FocusGrid, ActionButtonsGrid } from '#client/components/Grid';
import { classes, VGSInputCSS, VGSInputStyled } from '#client/components/VGSInput';
import { SUPPORT_PHONE } from '#client/constants/contacts';
import { CVV_ERROR_MESSAGE, EXPIRATION_DATE_ERROR_MESSAGE } from '#client/constants/messages';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useCardsContext from '#client/hooks/useCardsContext';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useFormStep from '#client/hooks/useFormStep';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import useVGSCollectUnit from '#client/hooks/useVGSCollectUnit';
import { TRACK_FAILED_ACTIVATION_MUTATION } from '#client/mutations';
import { isAllFieldsValid, isShowErrorMessage } from '#client/utils/vgs';
import { CARD_STATUS } from '#graphql/cards/constants';

import { ActiveCardInputsStyled, Wrapper } from './ActivateCardForm.styles';

import { CVV_FIELD_NAME, EXPIRATION_DATE_FILED_NAME, SET_PIN } from '../constants/card';

const regexExpirationDate = '(\\d{2})\\s\\/\\s(\\d{2})';

export default function ActivateCardForm({ handleClose }) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId } = useUnitAccountId();
  const [form, setForm] = useState(null);
  const captureException = useCaptureException();
  const { vgsCollect } = useVGSCollectUnit();

  const [formIsValid, setFormIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCvvErrorMessage, setShowCvvErrorMessage] = useState(false);
  const [showExpirationDateErrorMessage, setShowExpirationDateErrorMessage] = useState(false);
  const { getCustomerToken: getCardToken } = useCustomerToken({
    type: TOKEN_TYPES.CARDS_SENSITIVE_WRITE
  });
  const { getCustomerToken: getCommonToken } = useCustomerToken({ type: TOKEN_TYPES.COMMON });
  const {
    physicalCard,
    refetchCard,
    loading: loadingCard,
    error: errorCard,
    notHasPhysicalCard
  } = useCardsContext();
  const { setCurrentFormStep } = useFormStep();
  const activateButtonId = useEnterSubmit();
  const { createErrorToast, createSuccessToast, createCommonErrorToast } = useToastContext();
  const physicalCardStatus = physicalCard?.status;

  const name = `${physicalCard?.holder?.firstName ?? ''} ${physicalCard?.holder?.lastName ?? ''}`;

  const [trackFailActivate, { loading: isTrackFailActivateLoading }] = useMutation(
    TRACK_FAILED_ACTIVATION_MUTATION,
    {
      noThrow: true
    }
  );

  const isLoading = isSubmitting || isTrackFailActivateLoading;

  useEffect(() => {
    const isCloseModal =
      errorCard ||
      (!loadingCard && physicalCardStatus && physicalCardStatus !== CARD_STATUS.INACTIVE) ||
      notHasPhysicalCard;

    if (isCloseModal) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCard, physicalCardStatus, errorCard]);

  const initForm = (vgsCollectForm) => {
    const tempForm = vgsCollectForm.init((state) => {
      setFormIsValid(isAllFieldsValid(state));
      setShowCvvErrorMessage(isShowErrorMessage(CVV_FIELD_NAME, state));
      setShowExpirationDateErrorMessage(isShowErrorMessage(EXPIRATION_DATE_FILED_NAME, state));
    });

    tempForm.field('#cc-cvv2', {
      type: 'card-security-code',
      name: CVV_FIELD_NAME,
      maxLength: 3,
      validations: ['required', 'validCardSecurityCode'],
      css: VGSInputCSS,
      classes,
      autoFocus: true,
      autoComplete: 'off',
      inputMode: 'numeric'
    });

    /**
     * Currently, Unit only support YYYY-MM for activation card
     * But VGS Collect doesn't support convert date format from MM / YY to YYYY-MM
     * So we need to do it manually and hardcode 20 as first two digits of year
     */
    const twoFirstDigitsOfCurrentYear = dayjs().format('YYYY').slice(0, 2);
    const serializerDateFormat = `${twoFirstDigitsOfCurrentYear}$2-$1`;

    tempForm.field('#cc-expiration-date', {
      type: 'card-expiration-date',
      name: EXPIRATION_DATE_FILED_NAME,
      css: VGSInputCSS,
      placeholder: 'MM / YY',
      yearLength: '2',
      classes,
      validations: ['required', 'validCardExpirationDate', `/${regexExpirationDate}/`],
      serializers: [tempForm.SERIALIZERS.replace(regexExpirationDate, serializerDateFormat)],
      autoComplete: 'off',
      inputMode: 'numeric'
    });

    setForm(tempForm);
  };

  useEffect(() => {
    if (!loadingCard && physicalCardStatus && !form && vgsCollect) {
      initForm(vgsCollect);
    }
  }, [form, loadingCard, physicalCardStatus, vgsCollect]);

  const handleTrackFailActivate = async () => {
    const commonToken = await getCommonToken();

    if (!commonToken) {
      handleClose();
      return;
    }

    const response = await trackFailActivate({
      variables: {
        input: {
          identifier: {
            businessUuid: businessEntityUuid,
            unitAccountId
          },
          id: physicalCard?.id,
          customerToken: commonToken
        }
      }
    });

    // Show error toast if track failed
    const error = response?.error;
    if (error) {
      createCommonErrorToast({ error });
      return;
    }

    const failureActivationCount = response?.data?.trackFailedActivation?.failureActivationCount;

    switch (failureActivationCount) {
      case 1:
        createErrorToast({
          title: 'Enter the correct card info',
          description: 'The CVV or expiration date is incorrect. Please check and try again.'
        });
        break;
      case 2:
        createErrorToast({
          title: 'Please check your card and try again',
          description: 'After 3 tries, your card will be locked for 24 hrs for security purposes.'
        });
        break;
      case 3:
        // refetch card to show lock card banner
        refetchCard();
        // Should close modal before show toast, because close modal will clear toasts
        handleClose();
        createErrorToast({
          title: 'Your card is locked for 24 hours',
          description: 'You can activate your card in 24 hours or contact card support.'
        });
        break;
      default:
        break;
    }
  };

  const handleResponse = async (status, data) => {
    const defaultError = new Error(
      `Activate card failed with status code: ${status} and data: ${JSON.stringify(data)}`
    );

    switch (status) {
      case 200:
        createSuccessToast({
          title: 'Your card is ready to use',
          description: 'Now you can use your card wherever Visa is accepted.'
        });
        refetchCard().finally(() => {
          setCurrentFormStep(SET_PIN);
        });
        break;
      /**
       * 400 - Incorrect CVV or Expiration Date
       * If you want to test this error, you need to:
       *  - Remove 'validCardExpirationDate' from the Expiration Date field
       *  - Input invalid expiration date: 32/23
       */
      case 400:
        handleTrackFailActivate();
        break;
      default:
        // Toast errors for unit API, don't need to pass error to toast
        captureException(defaultError, { level: 'warning' });
        createCommonErrorToast();
        break;
    }
  };

  const handleFormSubmit = async () => {
    setIsSubmitting(true);
    const customerToken = await getCardToken();

    if (!customerToken) {
      handleClose();
      return;
    }

    form.submit(
      `/cards/${physicalCard?.id}/activate`,
      {
        mapDotToObject: 'true',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Authorization: `Bearer ${customerToken}`
        }
      },
      (status, data) => {
        handleResponse(status, data);
        setIsSubmitting(false);
      }
    );
  };

  /***
   * Should keep loading state when card is not INACTIVE
   * It will close modal soon when card is not INACTIVE
   */
  if (loadingCard || physicalCardStatus !== CARD_STATUS.INACTIVE) {
    return (
      <DialogContent>
        <LinearProgress />
      </DialogContent>
    );
  }

  return (
    <>
      <DialogContent>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper spacing="32px">
              <Stack spacing="16px">
                <Heading
                  family="secondary"
                  size="3xl"
                  weight="bold"
                >
                  Ready to activate your card?
                </Heading>
                <Text
                  color="secondary"
                  size="lg"
                >
                  Let&apos;s go. To start using your card now, enter your info or give us a call.
                </Text>
              </Stack>
              <ActiveCardInputsStyled spacing="32px">
                <Text size="xl">{name}</Text>
                <Stack spacing="32px">
                  <Stack spacing="4px">
                    <VGSInputStyled
                      aria-label="CCV field"
                      id="cc-cvv2"
                    >
                      <label>CVV</label>
                    </VGSInputStyled>
                    {showCvvErrorMessage && (
                      <Text
                        color="red"
                        size="md"
                      >
                        {CVV_ERROR_MESSAGE}
                      </Text>
                    )}
                  </Stack>
                  <Stack spacing="4px">
                    <VGSInputStyled
                      aria-label="Expiration date field"
                      id="cc-expiration-date"
                    >
                      <label>Expiration Date</label>
                    </VGSInputStyled>
                    {showExpirationDateErrorMessage && (
                      <Text
                        color="red"
                        size="md"
                      >
                        {EXPIRATION_DATE_ERROR_MESSAGE}
                      </Text>
                    )}
                  </Stack>
                </Stack>
              </ActiveCardInputsStyled>
              <Stack spacing="8px">
                <Text
                  size="xl"
                  weight="medium"
                >
                  Call us
                </Text>
                <Text
                  color="secondary"
                  size="lg"
                >
                  We&apos;re on Central Time. Call us on weekdays from 8am to 8pm or Sundays from
                  10am to 7pm.
                </Text>
                <Link
                  href={`tel:${SUPPORT_PHONE}`}
                  size="lg"
                  weight="medium"
                >
                  {SUPPORT_PHONE}
                </Link>
              </Stack>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContent>
      <ActionButtonsGrid>
        <LoadingButton
          color="primary"
          disabled={!formIsValid || isSubmitting || !form}
          fullWidth
          id={activateButtonId}
          loading={isLoading}
          onClick={handleFormSubmit}
          size="large"
          variant="contained"
        >
          Activate
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}

ActivateCardForm.propTypes = {
  handleClose: PropTypes.func.isRequired
};
